import { useMemo, useState } from 'react';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { Building2, FileText, Lock, MoreHorizontal, Plus, Star } from 'lucide-react';
import { useAuthStore } from '@/stores/auth.store';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/atoms/dropdown-menu';
import {
  useEstimateDocTemplates,
  useCloneEstimateDocTemplate,
  useSetDefaultEstimateDocTemplate,
  useDeleteEstimateDocTemplate,
} from '@/hooks/estimateDocTemplates';
import {
  useEstimateDocTemplateSettings,
  useUpdateEstimateDocTemplateSettings,
} from '@/hooks/settings/useEstimateDocTemplateSettings';
import { useHasPermission } from '@/hooks/usePermissions';
import { Switch } from '@/components/atoms/switch';
import { Tabs, TabsList, TabsTrigger } from '@/components/atoms/tabs';
import type { EstimateDocTemplateSummary } from '@/types/estimateDocTemplate';
import { EmailDesignsSection } from '@/components/documents/emails/EmailDesignsSection';
import { EMAIL_TEMPLATE_KINDS, type EmailTemplateKind } from '@/types/emailTemplate';

type DesignsSearch = {
  tab?: 'documents' | 'emails';
  kind?: EmailTemplateKind;
  doc?: 'ESTIMATE' | 'INVOICE';
};

function isEmailTemplateKind(value: unknown): value is EmailTemplateKind {
  return EMAIL_TEMPLATE_KINDS.includes(value as EmailTemplateKind);
}

export const Route = createFileRoute('/_dashboard/dashboard/settings/document-templates/')({
  // The page's tab / email kind / document family live in the URL, so a deep
  // link (Settings → Reminders → a reminder's design) lands on the right tab,
  // and coming back from the builder returns to where the person left.
  validateSearch: (search: Record<string, unknown>): DesignsSearch => ({
    ...(search.tab === 'emails' ? { tab: 'emails' as const } : {}),
    ...(isEmailTemplateKind(search.kind) ? { kind: search.kind } : {}),
    ...(search.doc === 'INVOICE' ? { doc: 'INVOICE' as const } : {}),
  }),
  beforeLoad: () => {
    const { permissions } = useAuthStore.getState();
    if (permissions.length > 0 && !permissions.includes('estimates:view')) {
      throw redirect({ to: '/dashboard/home' });
    }
  },
  component: DocumentTemplatesPage,
});

const eyebrow = 'font-mono text-[9.5px] font-medium uppercase tracking-[0.1em] text-[#6b6b70]';
const crumb = 'font-mono text-[9.5px] font-medium uppercase tracking-[0.15em] text-[#6b6b70]';
const GRID = 'grid grid-cols-[minmax(0,1fr)_132px_96px_40px] items-center gap-[14px]';

type Tab = 'ALL' | 'PUBLISHED' | 'DRAFT';

type DocKind = 'ESTIMATE' | 'INVOICE';

type TopTab = 'documents' | 'emails';

function DocumentTemplatesPage() {
  const navigate = useNavigate();
  const search = Route.useSearch();
  const topTab: TopTab = search.tab === 'emails' ? 'emails' : 'documents';
  const emailKind: EmailTemplateKind = search.kind ?? 'ESTIMATE_SEND';
  const docType: DocKind = search.doc === 'INVOICE' ? 'INVOICE' : 'ESTIMATE';
  const setSearch = (patch: Partial<DesignsSearch>) =>
    void navigate({
      to: '/dashboard/settings/document-templates',
      search: { ...search, ...patch },
      replace: true,
    });
  const setTopTab = (t: TopTab) => setSearch({ tab: t === 'emails' ? 'emails' : undefined });
  const setEmailKind = (k: EmailTemplateKind) => setSearch({ kind: k });
  const setDocType = (d: DocKind) => setSearch({ doc: d === 'INVOICE' ? 'INVOICE' : undefined });
  const { data: templates, isLoading } = useEstimateDocTemplates({ docType });
  const clone = useCloneEstimateDocTemplate();
  const setDefault = useSetDefaultEstimateDocTemplate();
  const del = useDeleteEstimateDocTemplate();
  // The switch is per document family — invoices toggle independently of
  // estimates, each defaulting OFF.
  const settings = useEstimateDocTemplateSettings(docType);
  const updateSettings = useUpdateEstimateDocTemplateSettings(docType);
  const canEdit = useHasPermission('company:edit');
  // Mirror the backend guards on estimate-doc-templates: create / edit (clone +
  // set-default) / delete each require their own estimates:* permission.
  const canCreate = useHasPermission('estimates:create');
  const canEditTemplate = useHasPermission('estimates:edit');
  const canDelete = useHasPermission('estimates:delete');
  const enabled = settings.data?.enabled ?? false;
  const [tab, setTab] = useState<Tab>('ALL');

  const all = useMemo(() => templates ?? [], [templates]);
  const counts = {
    ALL: all.length,
    PUBLISHED: all.filter((t) => t.status === 'PUBLISHED').length,
    DRAFT: all.filter((t) => t.status === 'DRAFT').length,
  };
  const rows = useMemo(
    () => (tab === 'ALL' ? all : all.filter((t) => t.status === tab)),
    [all, tab],
  );

  const open = (id: string) =>
    navigate({ to: '/dashboard/settings/document-templates/$id', params: { id } });

  return (
    <div className="mx-auto flex h-[calc(100vh-3.5rem)] w-full max-w-5xl flex-col px-[22px] pb-4 pt-[18px]">
      {/* title row */}
      <div className="flex shrink-0 items-center gap-4">
        <div className="min-w-0">
          <div className={crumb}>Settings / Documents</div>
          <h1 className="mt-1 text-[22px] font-medium leading-none tracking-[-0.03em] text-[#0a0b0d]">
            Designs
          </h1>
        </div>
        <div className="flex-1" />
        {canCreate ? (
          <button
            type="button"
            onClick={() =>
              topTab === 'documents'
                ? navigate({
                    to: '/dashboard/settings/document-templates/new',
                    search: { docType },
                  })
                : navigate({
                    to: '/dashboard/settings/email-templates/new',
                    search: { kind: emailKind },
                  })
            }
            className="inline-flex h-[34px] items-center gap-[7px] rounded-lg border border-[#0a0b0d] bg-[#0a0b0d] px-3 text-[12.5px] font-medium text-[#f5f4ef] hover:bg-[#26272b]"
          >
            <Plus className="h-[14px] w-[14px]" />
            {topTab === 'documents' ? 'New design' : 'New email'}
          </button>
        ) : null}
      </div>

      {/* primary navigation: PDF documents vs send emails — underline tabs,
          visually distinct from the pill filters below (GitHub/Stripe pattern). */}
      <div className="mt-3 shrink-0 border-b border-[#e5e4de]">
        <Tabs value={topTab} onValueChange={(v) => setTopTab(v as TopTab)}>
          <TabsList variant="line" className="h-8 gap-4 p-0">
            <TabsTrigger value="documents" className="px-0 text-[13px]">
              Documents
            </TabsTrigger>
            <TabsTrigger value="emails" className="px-0 text-[13px]">
              Emails
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {topTab === 'emails' ? (
        <EmailDesignsSection kind={emailKind} setKind={setEmailKind} />
      ) : (
        <>
          {/* filter bar — surface, status, and the PDF-enable switch on one row */}
          <div className="mt-3 flex shrink-0 flex-wrap items-center gap-x-2 gap-y-2">
            <div className="inline-flex rounded-lg border border-[#e5e4de] bg-[#f5f4ef] p-[3px]">
              {(
                [
                  ['ESTIMATE', 'Estimates'],
                  ['INVOICE', 'Invoices'],
                ] as [DocKind, string][]
              ).map(([k, label]) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setDocType(k)}
                  className={`rounded-md px-3 py-[5px] text-[12px] font-medium transition-colors ${
                    docType === k
                      ? 'bg-white text-[#0a0b0d] shadow-[0_1px_2px_rgba(10,11,13,.08)]'
                      : 'text-[#6b6b70] hover:text-[#0a0b0d]'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className="flex items-center rounded-lg border border-[#d9d8d2] bg-white p-[3px]">
              {(
                [
                  ['ALL', `All ${counts.ALL}`],
                  ['PUBLISHED', `Published ${counts.PUBLISHED}`],
                  ['DRAFT', `Drafts ${counts.DRAFT}`],
                ] as [Tab, string][]
              ).map(([t, lbl]) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setTab(t)}
                  className={`h-[26px] rounded-[6px] px-[10px] text-[12px] font-medium ${
                    tab === t
                      ? 'bg-[#0a0b0d] text-[#f5f4ef]'
                      : 'text-[#6b6b70] hover:text-[#0a0b0d]'
                  }`}
                >
                  {lbl}
                </button>
              ))}
            </div>
            <div className="flex-1" />
            {/* Per-surface enable — invoices only: designs are the only estimate
                document system, so estimates have no switch. */}
            {docType === 'INVOICE' ? (
              <>
                <span
                  className="text-[12px]"
                  style={{ color: enabled ? '#2e4a2e' : '#6b6b70' }}
                  title="When on, an invoice with a design applied renders its PDF through it; when off, the classic invoice layout is used."
                >
                  Use for invoice PDFs
                </span>
                <Switch
                  checked={enabled}
                  disabled={!canEdit || settings.isLoading || updateSettings.isPending}
                  onCheckedChange={(next) => updateSettings.mutate({ enabled: next })}
                  aria-label="Enable document designs for invoice PDFs"
                />
              </>
            ) : null}
          </div>

          {/* table */}
          <div className="mt-[14px] flex min-h-0 flex-1 flex-col overflow-hidden rounded-[14px] border border-[rgba(10,11,13,0.08)] bg-white">
            <div
              className={`${GRID} ${eyebrow} h-[34px] border-b border-[#e5e4de] bg-[#fbfaf7] px-[18px]`}
            >
              <div>Design</div>
              <div>Status</div>
              <div className="text-right">Version</div>
              <div />
            </div>

            <div className="min-h-0 flex-1 overflow-auto">
              {isLoading ? (
                <div className="p-4">
                  {[0, 1, 2].map((i) => (
                    <div key={i} className="mb-2 h-[52px] animate-pulse rounded-lg bg-[#edece6]" />
                  ))}
                </div>
              ) : rows.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 text-center">
                  <FileText className="mb-3 h-8 w-8 text-[#c8c7c1]" />
                  <p className="text-sm font-medium text-[#0a0b0d]">
                    {tab === 'ALL' ? 'No document designs yet' : `No ${tab.toLowerCase()} designs`}
                  </p>
                  <p className="mt-1 max-w-sm text-[13px] text-[#6b6b70]">
                    Create one to customize the {docType === 'INVOICE' ? 'invoice' : 'estimate'}{' '}
                    your customers see. Until then,{' '}
                    {docType === 'INVOICE' ? 'invoices' : 'estimates'} use the built-in default.
                  </p>
                </div>
              ) : (
                rows.map((tpl) => (
                  <TemplateRow
                    key={tpl.id}
                    tpl={tpl}
                    onOpen={() => open(tpl.id)}
                    onClone={canEditTemplate ? () => clone.mutate({ id: tpl.id }) : undefined}
                    onSetDefault={
                      canEditTemplate ? () => setDefault.mutate({ id: tpl.id }) : undefined
                    }
                    onDelete={canDelete ? () => del.mutate({ id: tpl.id }) : undefined}
                    onUseInBu={() => navigate({ to: '/dashboard/settings/trades' })}
                  />
                ))
              )}
            </div>

            <div className="flex h-[38px] shrink-0 items-center gap-3 border-t border-[#e5e4de] bg-[#fbfaf7] px-[18px]">
              <span className="font-mono text-[9.5px] font-medium uppercase tracking-[0.1em] text-[#6b6b70]">
                {counts.ALL} designs · {all.filter((t) => t.isDefault).length} default ·{' '}
                {all.filter((t) => t.isSystemDefault).length} maintained by Notifi
              </span>
              <span className="flex-1" />
              <span className="text-[11.5px] text-[#6b6b70]">
                Publishing replaces the live document for every{' '}
                {docType === 'INVOICE' ? 'invoice' : 'estimate'} sent after that moment.
              </span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function StatusPill({ status }: { status: 'DRAFT' | 'PUBLISHED' }) {
  const published = status === 'PUBLISHED';
  return (
    <span
      className="inline-flex items-center gap-[5px] rounded-full border px-2 py-[3px] font-mono text-[9px] font-medium uppercase tracking-[0.09em]"
      style={
        published
          ? { background: '#e8ede5', color: '#2e4a2e', borderColor: '#a9bc97' }
          : { background: '#f2eadf', color: '#5a4424', borderColor: '#e3cb92' }
      }
    >
      <span
        className="h-[5px] w-[5px] rounded-full"
        style={{ background: published ? '#2e4a2e' : '#e0be7a' }}
      />
      {published ? 'Published' : 'Draft'}
    </span>
  );
}

function TemplateRow({
  tpl,
  onOpen,
  onClone,
  onSetDefault,
  onDelete,
  onUseInBu,
}: {
  tpl: EstimateDocTemplateSummary;
  onOpen: () => void;
  onClone?: () => void;
  onSetDefault?: () => void;
  onDelete?: () => void;
  onUseInBu: () => void;
}) {
  return (
    <div
      className={`${GRID} h-[66px] cursor-pointer border-b border-[#f3f2ed] px-[18px] hover:bg-[#f5f4ef]`}
      onClick={onOpen}
    >
      <div className="flex min-w-0 items-center gap-[11px]">
        <span className="flex h-[44px] w-[34px] shrink-0 items-center justify-center rounded-[4px] border border-[#d9d8d2] bg-white shadow-[0_1px_2px_rgba(10,11,13,.06)]">
          <FileText className="h-[14px] w-[14px] text-[#6b6b70]" />
        </span>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="truncate text-[13.5px] font-medium tracking-[-0.01em] text-[#0a0b0d]">
              {tpl.name}
            </span>
            {tpl.isDefault ? (
              <span className="inline-flex items-center gap-[5px] rounded-full border border-[#e3cb92] bg-[#f2eadf] px-2 py-[3px] font-mono text-[9px] font-medium uppercase tracking-[0.09em] text-[#5a4424]">
                <Star className="h-[9px] w-[9px] fill-[#e0be7a] text-[#5a4424]" />
                Default
              </span>
            ) : null}
            {tpl.isSystemDefault ? (
              <span className="inline-flex items-center gap-[5px] rounded-full border border-[#e5e4de] bg-[#edece6] px-2 py-[3px] font-mono text-[9px] font-medium uppercase tracking-[0.09em] text-[#6b6b70]">
                <Lock className="h-[9px] w-[9px]" />
                System
              </span>
            ) : null}
            {tpl.isSingleScope ? (
              <span className="inline-flex items-center gap-[5px] rounded-full border border-[#e5e4de] bg-[#edece6] px-2 py-[3px] font-mono text-[9px] font-medium uppercase tracking-[0.09em] text-[#6b6b70]">
                <Lock className="h-[9px] w-[9px]" />
                Single-scope
              </span>
            ) : null}
          </div>
          <div className="mt-px truncate text-[11.5px] text-[#6b6b70]">
            {tpl.isSystemDefault
              ? `What every ${tpl.docType === 'INVOICE' ? 'invoice' : 'estimate'} falls back to. Duplicate it to customize.`
              : tpl.isSingleScope
                ? 'One scope price, no line-item table. Structure is locked — restyle only the colours & fonts.'
                : `${tpl.docType === 'INVOICE' ? 'Invoice' : 'Estimate'} document · v${tpl.version}`}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-[6px]">
        <StatusPill status={tpl.status} />
        {tpl.status === 'PUBLISHED' && tpl.hasUnpublishedChanges ? (
          <span
            className="inline-flex items-center gap-[5px] rounded-full border px-2 py-[3px] font-mono text-[9px] font-medium uppercase tracking-[0.09em]"
            style={{ background: '#f2eadf', color: '#5a4424', borderColor: '#e3cb92' }}
            title="Saved edits that customers will not see until you publish"
          >
            <span className="h-[5px] w-[5px] rounded-full" style={{ background: '#e0be7a' }} />
            Unpublished edits
          </span>
        ) : null}
      </div>
      <div className="text-right font-mono text-[11px] text-[#2e2f33]">v{tpl.version}</div>
      <div className="flex justify-end" onClick={(e) => e.stopPropagation()}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label="Actions"
              className="flex h-7 w-7 items-center justify-center rounded-md text-[#c8c7c1] hover:bg-[#edece6] hover:text-[#0a0b0d]"
            >
              <MoreHorizontal className="h-[15px] w-[15px]" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={onOpen}>
              {tpl.isSystemDefault ? 'View' : 'Edit design'}
            </DropdownMenuItem>
            {tpl.status === 'PUBLISHED' ? (
              <DropdownMenuItem onClick={onUseInBu}>
                <Building2 className="h-4 w-4" />
                Use in a business unit
              </DropdownMenuItem>
            ) : null}
            {onClone ? <DropdownMenuItem onClick={onClone}>Duplicate</DropdownMenuItem> : null}
            {onSetDefault && !tpl.isDefault && tpl.status === 'PUBLISHED' ? (
              // Only a PUBLISHED design can be the default — the backend rejects
              // promoting a draft, so don't offer the action on draft rows.
              <DropdownMenuItem onClick={onSetDefault}>Set as default</DropdownMenuItem>
            ) : null}
            {onDelete && !tpl.isSystemDefault && !tpl.isSingleScope ? (
              <DropdownMenuItem onClick={onDelete} className="text-[#5e2222]">
                Delete
              </DropdownMenuItem>
            ) : null}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
